<template>
  <div class="comments-wrapper pt-6">
    <p
      v-if="data.nextToken"
      class="medium-p mb-0 mx-8 mb-2 text-center show-more-comments"
      @click="$emit('getComments', {
        scanIndexForward: false,
      })">
      <span class="pointer">
        Show Earlier Comments
      </span>
    </p>
    <v-row
      v-for="(comment, index) in filterComments"
      :id="`commentId_${comment.commentId}`"
      :key="comment.commentId"
      ref="commentsRows"
      data-test="project_comment_block"
      class="comments-wrapper-border-items"
      :class="{ 'mb-0' : index === data.comments.length -1,
                'comments-wrapper-border-items__edited': editedRowId === comment.commentId,
                'comments-wrapper-border-items__scrolled-comment': scrolledComment === comment.commentId,
      }"
      @mouseover="!$route.query.version ? hoverRowId=comment.commentId : hoverRowId=null"
      @mouseleave="hoverRowId=null">
      <div>
        <AvatarImage
          :avatar="avatars[getCreatorProp({ comment })]"
          :avatar-key="getCreatorProp({ comment })"
          :color="getCreatorColor({ comment })"
          :size="40" />
      </div>
      <v-col
        class="pa-0"
        sm="11"
        cols="12"
        :class="{'pl-3': $vuetify.breakpoint.smAndUp }">
        <v-row class="ma-0 d-flex justify-space-between align-center">
          <UserInfo
            :user-info="comment.creator">
            <template
              v-slot="{
                userProjectTeamRole: teamRole,
                userNameTitleAccordingToRole: userNameTitle
              }">
              <p
                data-test="project_comment_author_name"
                class="medium-p font-weight-medium mb-0"
                style="overflow-wrap: anywhere">
                {{ userNameTitle }}
                <span
                  v-if="teamRole"
                  class="subtitle-2">
                  {{ teamRole }}
                </span>
              </p>
            </template>
          </UserInfo>
          <p
            data-test="project_comments_time"
            class="small-p title-in-modal mb-0 d-flex">
            {{ setDateOfComment(comment) }}
            {{ showCommentEditedTitle(comment) }}
            <Actions
              v-if="editMode && !isDisabledComments"
              :hover-row-id="hoverRowId"
              :comment="comment"
              @editComment="editComment(comment)"
              @deleteComment="deleteComment(comment)" />
          </p>
        </v-row>
        <v-row class="ma-0 d-flex flex-column">
          <p
            data-test="project_comment_author_email"
            class="small-p title-in-modal mb-3 "
            :class="{ 'unread-container' : commentsUnread(comment) }">
            {{ getCreatorProp({comment, prop: 'email'}) }}
            <v-icon
              size="14"
              class="pl-2 pointer">
              {{ setCommentIcon(comment) }}
            </v-icon>
            <template
              v-if="showCellPointerBlock(comment) && !isDisabledComments">
              <v-tooltip
                bottom>
                <template #activator="{ on, attrs }">
                  <v-icon
                    data-test="project_comment_resolve_button"
                    :size="isShowResolveAction(comment) ? 22 : 16"
                    v-bind="attrs"
                    class="resolve-reveal-icon"
                    v-on="on"
                    @click="manageCellCommentResolved(comment)">
                    {{ setResolveIcon(comment) }}
                  </v-icon>
                </template>
                <span>{{ setResolveTooltip(comment) }}</span>
              </v-tooltip>
              <v-tooltip
                v-if="!isShowResolveAction(comment)"
                bottom>
                <template #activator="{ on, attrs }">
                  <v-icon
                    medium
                    v-bind="attrs"
                    class="resolve-reveal-icon"
                    v-on="on"
                    @click="manageRevealing(comment)">
                    {{ setExpandedIcon(comment.id) }}
                  </v-icon>
                </template>
                <span>Reveal</span>
              </v-tooltip>
            </template>
          </p>
        </v-row>
        <v-row class="ma-0 flex-column comment-file">
          <p
            v-if="comment.cellPointer"
            class="small-p title-in-modal">
            Related to <strong>{{ comment.cellPointer }}</strong>
          </p>
          <p
            v-if="comment.commentType !== 'attachment'"
            v-safe-html="formattedTextareaValueForDiv(comment) || ''"
            v-linkified
            data-test="project_comment_text"
            class="small-p mb-0"
            :class="{
              'resolved-comment' : comment.commentResolved,
              'resolved-comment__expanded': expandedComments.includes(comment.id),
            }"
            style="overflow-wrap: anywhere" />
          <a
            v-else-if="comment.commentType === 'attachment'"
            :href="`${getFileUrlForDownload(comment) ? `/getFile?fileId=${getFileUrlForDownload(comment)}`: ''}`"
            target="_blank"
            :class="{
              'resolved-comment' : comment.commentResolved,
              'resolved-comment__expanded': expandedComments.includes(comment.commentId),
            }"
            class="comment-file-container">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-row
                  no-gutters
                  v-bind="attrs"
                  class="comment-file-container__content pointer"
                  v-on="on">
                  <v-col
                    class="pa-0"
                    cols="12">
                    <img
                      v-show="!getThumbnailObjectKey(comment) || !getThumbnail(getThumbnailObjectKey(comment))"
                      src="@/assets/icons/file-icon.svg">
                    <p
                      v-if="!getThumbnailObjectKey(comment)"
                      class="small-p mb-0 overflow-dots"
                      style="max-width: 105px">
                      {{ getFileName(comment) }}
                    </p>
                    <img
                      v-if="getThumbnailObjectKey(comment)"
                      v-lazy="{
                        src: getThumbnail(getThumbnailObjectKey(comment)),
                        loading: require('@/assets/icons/file-icon.svg'),
                      }"
                      class="img-screen thumbnail-img">
                  </v-col>
                  <v-col
                    class="pa-0"
                    cols="12">
                    <p
                      class="small-p download-p mb-0 d-flex"
                      :style="{
                        justifyContent: getThumbnailObjectKey(comment) ? 'flex-start' : 'initial',
                      }">
                      <a
                        :href="`/getFile?fileId=${getFileUrlForDownload(comment)}&force_update=true`"
                        class="ml-0"
                        @click.prevent="downloadFile(comment)">
                        Download
                      </a>
                    </p>
                  </v-col>
                </v-row>
              </template>
              <span>
                {{ getFileName(comment) }}
              </span>
            </v-tooltip>
          </a>
        </v-row>
      </v-col>
    </v-row>
    <p
      v-if="data.nextTokenForUnread && !type"
      class="medium-p mb-0 mt-2 mx-8 text-center show-more-comments"
      @click="$emit('getComments', {
        scanIndexForward: true,
      })">
      <span class="pointer">
        Show the Latest Comments
      </span>
    </p>
  </div>
</template>
<script>
import {
  mapState,
  mapActions,
  mapGetters,
} from 'vuex';
import AppCommentsActions from '@/components/App/AppComments/AppCommentsActions';
import linkify from 'vue-linkify';
import CommentsResources from '@/mixins/CommentsResources';
import {
  COMMENT_RESOURCE_TYPE, COMMENTS_RESOURCES,
} from '@/constants/comments';
export default {
  name: 'AppCommentsList',
  components: {
    Actions: AppCommentsActions,
    UserInfo: () => import('@/components/App/AppShareModal/AppShareModalUserInfo'),
  },
  directives: {
    linkified: linkify,
  },
  mixins: [CommentsResources],
  props: {
    allowResolveComments: {
      type: Boolean,
      default: false,
    },
    filesSrc: {
      type: Object,
      default: () => {
      },
    },
    editMode: {
      type: Boolean,
      default: true,
    },
    editedRowId: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => ({
        comments: [],
        nextToken: null,
      }),
    },
    type: {
      type: String,
      default: null,
    },
    rowId: {
      type: String,
      default: null,
    },
    scrolledComment: {
      type: String,
      default: null,
    },
    isDisabledComments: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    hoverRowId: null,
    expandedComments: [],
  }),
  computed: {
    ...mapState(['avatars', 'userInfo']),
    ...mapGetters('Comments', ['slantV2']),
    ...mapState('Comments', ['hideResolvedCollectionCommentsV2', 'collectionCommentsListV2', 'hideResolvedProjectCommentsV2', 'projectCommentsListV2']),
    slant() {
      return this.slantV2(this.rowId, COMMENTS_RESOURCES[COMMENT_RESOURCE_TYPE.COLLECTION]);
    },
    hideResolvedComments() {
      const { currentResource } = this;
      return this[currentResource.hideResolvedToggle];
    },
    filterComments() {
      if (this.hideResolvedComments) {
        return this.data.comments.filter(el => !el.commentResolved);
      }
      return this.data.comments;
    },
  },
  destroyed() {
    this.expandedComments = [];
    this.$emit('update:filesSrc', {
    });
  },
  methods: {
    ...mapActions(['getFileFromS3']),
    manageCellCommentResolved(comment) {
      this.$emit('manageCellCommentResolved', comment);
    },
    showCellPointerBlock({ cellPointer }) {
      return cellPointer;
    },
    getCreatorColor({ comment }) {
      return this.getCreatorProp({
        comment, prop: 'id',
      })?.substring(0, 6);
    },
    getCreatorProp({ comment, prop = 'picture' }) {
      return comment.creator && comment.creator[prop];
    },
    getThumbnail(id) {
      return this.filesSrc[id];
    },
    renderAttachment(comment) {
      return comment?.attachments[0];
    },
    getThumbnailObject(comment) {
      const thumbnail = comment?.attachments[0]?.thumbnails;
      return thumbnail?.small || thumbnail?.large || null;
    },
    getThumbnailObjectKey(comment) {
      return this.getThumbnailObject(comment)?.key || '';
    },
    getFileUrlForDownload(comment) {
      const file = this.renderAttachment(comment);
      if (!file?.key) {
        return '';
      }
      return file?.key.split('/').slice(-2).join('/');
    },
    getFileName(comment) {
      return this.renderAttachment(comment)?.filename || '';
    },
    setExpandedIcon(id) {
      return this.expandedComments.includes(id) ?
        'mdi-chevron-up' : 'mdi-chevron-down';
    },
    setResolveIcon(comment) {
      return this.isShowResolveAction(comment) ?
        'mdi-checkbox-marked-circle' : 'mdi-history';
    },
    isShowResolveAction({ commentResolved }) {
      return !commentResolved;
    },
    setResolveTooltip(comment) {
      if (!this.editMode) {
        return 'Go to row comments to resolve or unresolve the comment';
      }
      return this.isShowResolveAction(comment) ?
        'Resolve' : 'Re-open';
    },
    showCommentEditedTitle({ dateModified }) {
      let title = undefined;
      if (dateModified) {
        title = '(edited)';
      }
      return title;
    },
    commentsUnread({
      commentId = null, creator = {
      },
    } = {
    }) {
      const {
        type,
        slant,
        userInfo,
        allowResolveComments,
      } = this;
      const { unreadMessageIDs } = slant ?? [];
      const unreadComment = unreadMessageIDs?.includes(commentId);
      const useInGeneralChat = allowResolveComments;
      return unreadComment && (!type || useInGeneralChat) && userInfo.email !== creator?.email;
    },
    setDateOfComment(comment) {
      const { dateModified, createdDate } = comment;
      const date = dateModified ? dateModified : createdDate;
      return this.$moment(date).fromNow();
    },
    async downloadFile(comment) {
      this.getFileFromS3({
        key: this.getFileUrlForDownload(comment),
        forceUpdate: true,
        fileName: this.getFileName(comment),
      });
    },
    manageRevealing({ id }) {
      const { expandedComments } = this;
      if (expandedComments.includes(id)) {
        this.expandedComments = expandedComments.filter(commentId => commentId !== id);
      } else {
        this.expandedComments = [...expandedComments, id];
      }
    },
    deleteComment(comment) {
      this.$emit('deleteComment', comment);
    },
    editComment(comment) {
      this.$emit('editComment', comment);
    },
    setCommentIcon({ commentType, privacy }) {
      let icon = '';
      if (privacy === 'private') {
        icon = commentType === 'offer' ? 'mdi-currency-usd' : 'mdi-lock-outline';
      } else if (commentType === 'attachment') {
        icon = 'mdi-attachment';
      }
      return icon;
    },
    formattedTextareaValueForDiv(comment) {
      try {
        if (comment.message) {
          let formattedString = comment.message.replace(/\r?\n/g, '<br>');
          if (comment?.mentioned) {
            comment.mentioned.forEach((user) => {
              if (user) {
                const getAllIndexes = (arr, val) => {
                  const indexes = [];
                  let i = -1;
                  while ((i = arr.indexOf(val, i + 1)) != -1) {
                    indexes.push(i);
                  }
                  return indexes;
                };
                const indexes = getAllIndexes(formattedString, `@${user.email}`);
                indexes.forEach((foundIndex, indexOfArr) => {
                  let index = foundIndex;
                  if (indexOfArr !== 0) {
                    index = foundIndex + indexOfArr * 7;
                  }
                  if (index >= 0) {
                    formattedString = `${formattedString.slice(0, index)}<a>${formattedString
                      .slice(index, index + user.email.length + 1)}</a>${formattedString
                      .slice(index + user.email.length + 1)}`;
                  }
                });
              }
            });
          }
          return formattedString;
        }
        return '';
      } catch (e) {
        console.log(e);
      }
    },
  },
};
</script>
<style scoped lang="scss">
.comments-wrapper {
  display: grid;
  gap: 13px;
  &__cell-title {
    position: sticky;
    top: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    z-index: 1;
    background: white;
    text-align: center;
  }
  &-border-items {
    border-bottom: 1px solid #E2E4E4;
    width: 100%;
    margin: 0;
    padding: 0px 20px 16px 20px;
    display: grid;
    grid-template-columns: auto 1fr;
    &:last-child {
      border-bottom: none;
    }
    &__edited {
      border: 1px solid lightBlue;
      opacity: 0.8;
    }
    &__scrolled-comment {
      background: #f3f3f9;
    }
  }
  .resolve-reveal-icon {
    margin-left: 4px;
    &.mdi-checkbox-marked-circle:hover {
      color: var(--v-success-base);
    }
  }
}
.show-more-comments {
  color: var(--v-lightBlue-base);
  &:hover {
    opacity: 0.6;
  }
}
.resolved-comment, .resolved-comment br {
  &__expanded {
    display: block;
  }
  display: none;
  color: var(--v-mainGrey-base);
}
.comment-file {
  display: grid;
  justify-content: start;
  gap: 5px;
  &-container {
    max-width: 105px;
    &__content {
      display: grid;
      img {
        border-radius: 5px;
      }
    }
  }
}
.unread-container {
  position: relative;
  &::after {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    -moz-border-radius: 7.5px;
    -webkit-border-radius: 7.5px;
    border-radius: 7.5px;
    background-color: var(--v-lightBlue-base);
    position: absolute;
    right: 0;
    bottom: 0;
  }
}
</style>
